import {
  HeadingProps,
  Heading,
  HStack,
  Box,
  Separator,
  Icon,
  VStack,
  Stack,
  List
} from '@chakra-ui/react';
import { FC, ReactNode } from 'react';
import SearchResultItem from './SearchResultItem';
import { TSearchResultSection } from '../../utils/search/types';
import TbBooks from '../icons/tabler/TbBooks';
import TbBookOff from '../icons/tabler/TbBookOff';
import { FaBook } from '@react-icons/all-files/fa/FaBook';

/**
 * The search menu section title component for displaying a search result section title.
 */
export const SearchResultSectionTitle: FC<
  HeadingProps & {
    title: string;
    idx: number;
    icon?: ReactNode;
  }
> = ({ title, idx, icon, ...props }) => {
  const heading = (
    <Heading
      key={-1}
      // size, not fontSize: see `siteHeadingSizes` in styles/theme/recipes.
      // It stays ahead of the `{...props}` spread, and a caller's fontSize now
      // wins at every width instead of only below 768px, because the selected
      // size emits no media rule for font-size.
      size="menu-group"
      {...(!icon && {
        mb: 2,
        mt: idx === 0 ? 2 : 5
      })}
      color="components.menu.groupTitle.color"
      {...props}
    >
      {title}
    </Heading>
  );

  if (icon) {
    return (
      <HStack
        mb={2}
        mt={idx === 0 ? 2 : 5}
        css={{
          // One ampersand: emotion expands each `&` to this element's own
          // class, so `& & svg` was `.css-x .css-x svg` and never matched.
          '& svg': {
            stroke: props.color ?? 'components.menu.groupTitle.color'
          }
        }}
      >
        {heading}
        {icon}
      </HStack>
    );
  }
  return heading;
};

/**
 * The search menu section component for displaying a search result section containing multiple search result items.
 */
export const SearchResultSection: FC<{
  section: TSearchResultSection;
  icon?: ReactNode;
  idx: number;
  query: string;
  defaultHighlight?: boolean;
  onItemClickCapture?: () => void;
  isDocs?: boolean;
}> = ({
  section,
  idx,
  icon,
  query,
  defaultHighlight,
  onItemClickCapture,
  isDocs
}) => {
  const pageToWithoutHash = (page: string) => {
    const hashIndex = page.indexOf('#');
    return hashIndex === -1 ? page : page.slice(0, hashIndex);
  };

  return (
    <Stack key={idx} w="full" gap="1">
      {isDocs && (
        <SearchResultItem
          item={{
            title: section.title,
            to: section.results?.[0]?.to
              ? pageToWithoutHash(section.results?.[0]?.to)
              : '',
            description: ''
          }}
          query={query}
          id={-1}
          icon={<FaBook />}
        />
      )}
      {/* <SearchResultSectionTitle title={section.title} idx={idx} icon={icon} /> */}
      {/* <Separator /> */}
      <List.Root as="ul" listStyleType="none" ml={isDocs ? 4 : 0}>
        <List.Item>
          {section.results.map((result, i) => (
            <SearchResultItem
              item={result}
              query={query}
              id={idx + i}
              key={i}
              defaultFocus={defaultHighlight && i === 0}
              onClickCapture={onItemClickCapture}
              icon={section.resultIcon ?? icon}
              isDocs={isDocs}
            />
          ))}
        </List.Item>
      </List.Root>
    </Stack>
  );
};
